import { reactive, ref } from "vue";
import { defineStore } from "pinia";
import * as API from "@/api/API";
import type { Address } from "./Address";
import type { SetOptional } from "type-fest";

export type Subscription = {
  created: string;
  expired: boolean;
  id: number;
  seats: number;
  editable: boolean;
  billingAddress: Address;
  paymentPeriod: "year" | "month";
  paymentMethod: "invoice" | "stripe";
  plan: "single" | "organization";
  planInfo: {
    id: string;
    subscriptionType?: string;
    canAddSeats?: boolean;
    canAddWorkspaces?: boolean;
    label?: string;
  };
  remainingDays: number | null;
  renewAutomatically: boolean;
  renewalDate: string | null;
  validUntil: string | null;
  expiredAt: string | null;
  lexofficeQuoteId: string | null;
  upgrades: string[];
  workspaceLimit: number | null;
  workspaceLimitOnRenew: number | null;
  nextRenew: { seats: number; upgrades: string[], years: number },
  organization: {
    id: number;
    slug: string;
    name: string;
  } | null;
  lastPaymentDate: string | null;
  usesStandardPrices: boolean;
};

export type SubscriptionRequest = {
  id: number;
  created: string;
  plan: "single" | "organization";
  seats: number;
  upgrades: string[];
  billingAddress: Address;
  validUntil: string;
  startDate: string;
  endDate: string;
  years: number;
  renewAutomatically: boolean;
  confirmedAt: string | null;
  lexofficeQuoteNumber: string | null;
  subscriptionId: number | null;
};

const cache = reactive({} as { [id: number]: Subscription });

export const useSubscriptions = defineStore("subscriptions", () => {
  const loading = ref(false);

  async function getSubscriptions() {
    loading.value = true;
    try {
      const subscriptions = await API.get<Subscription[]>("/v1/subscriptions");
  
      // move expired subscriptions to the end
      subscriptions.sort((a, b) => {
        return a.expired === b.expired ? 0 : a.expired ? 1 : -1;
      });
  
      emptyCache();
      subscriptions.forEach(sub => {
        cache[sub.id] = sub;
      });
  
      return subscriptions;
    } finally {
      loading.value = false;
    }
  }

  async function createSubscription(data: Partial<Subscription> & {
    paymentMethod: "invoice";
    seats: number;
    plan: Subscription["plan"];
    billingAddress: Address;
    upgrades: string[]
  }) {
    const { subscription, invoiceDocument } = await API.post<{ subscription: Subscription, invoiceDocument: string }>("/v1/subscriptions", {
      data: data
    });

    cache[subscription.id] = subscription;

    // decode invoice PDF from base64
    // decode base64 string, remove space for IE compatibility
    const binary = atob(invoiceDocument.replace(/\s/g, ''));
    const len = binary.length;
    const buffer = new ArrayBuffer(len);
    const view = new Uint8Array(buffer);
    for (let i = 0; i < len; i++) {
        view[i] = binary.charCodeAt(i);
    }

    // create the blob object with content-type "application/pdf"               
    const invoiceBlob = new Blob( [view], { type: "application/pdf" });

    return { subscription, invoiceDocument: invoiceBlob };
  }


  async function updateSubscription(subscriptionId: number, data: Partial<Subscription>) {
    const updatedSub = await API.patch<Subscription>(`/v1/subscriptions/${subscriptionId}`, {
      data: data
    });

    updateSubscriptionInCache(subscriptionId, updatedSub);

    return updatedSub;
  }

  async function modifySubscription(subscription: Subscription, newSettings: {
    seats?: number | null,
    upgrades: string[]
  }) {
    const modifiedSub = await API.post<Subscription>(`/v1/subscriptions/${subscription.id}/modify`, {
      data: newSettings
    });

    updateSubscriptionInCache(subscription.id, modifiedSub);

    return modifiedSub;
  }

  async function cancelSubscription(subscriptionId: number) {
    const canceled = await API.post<Subscription | null>(`/v1/subscriptions/${subscriptionId}/cancel`);

    updateSubscriptionInCache(subscriptionId, canceled);

    return canceled;
  }

  async function reactivateSubscriptionRenewal(subscriptionId: number) {
    const updated = await API.post(`/v1/subscriptions/${subscriptionId}/reactivate_renewal`);
    updateSubscriptionInCache(subscriptionId, updated);
    return updated;
  }

  /**
   * Renew a subscription that has expired.
   * This only works for subscriptions that have been ordered via stripe.
   * Creates a new stripe checkout session.
   */
  async function renewSubscription(subscription: Subscription) {
    const subscriptionId = subscription.id;
    const session = await API.post<{ url: string; sessionId: string }>(`/v1/subscriptions/${subscriptionId}/stripe_renew`);

    // Open the checkout page in the current tab
    location.href = session.url;
  }

  /**
   * Create a subscription for the user. Will be inactive until payment is received.
   */
  async function createSubscriptionRequest(data: {
    plan: "single" | "organization",
    payment: "invoice" | "quote" | "stripe",
    billingAddress: SetOptional<Address, "company" | "vatId">,
    seats?: number,
    upgrades: string[],
    startDate: string | null,
    years: number,
    renewAutomatically: boolean
  }) {
    const subscriptionRequest = await API.post<SubscriptionRequest>("/v1/subscription-requests", {
      data: data
    });

    return subscriptionRequest;
  }

  async function getSubscriptionRequests() {
    return API.get<SubscriptionRequest[]>("/v1/subscription-requests");
  }

  async function cancelSubscriptionRequest(requestId: number) {
    return API.destroy(`/v1/subscription-requests/${requestId}`);
  }

  async function requestExtensionQuote(subscriptionId: number, options: { years: number }) {
    return API.post<SubscriptionRequest>(`/v1/subscriptions/${subscriptionId}/request_extension_quote`, {
      data: options
    });
  }

  async function goToStripeCheckout(options: { planId: string, seats: number, upgrades: string[] }, { sameWindow = false } = {}) {
    const session = await API.post<{ url: string; sessionId: string }>(`/v1/subscriptions/stripe_checkout`, {
      data: options
    });

    if (sameWindow) {
      // Open the checkout page in the current tab
      location.href = session.url;
    } else {
      // Open the checkout page in a new tab
      window
        .open(session.url, "_blank")
        ?.focus();
    }


    return session;
  }

  async function getStripeCheckoutStatus(sessionId: string) {
    return API.get<{ status: "open" | "complete" | "expired"; paymentStatus: "paid" | "unpaid"; subscriptionId: number | null }>(`/v1/subscriptions/stripe_checkout_status?session_id=${sessionId}`);
  }

  // Internal functions

  function updateSubscriptionInCache(id: number, subscription: Subscription | null) {
    if (subscription === null) {
      delete cache[id];
      return;
    }

    cache[id] = subscription;
  }

  function emptyCache() {
    for (const id in cache) {
      delete cache[id];
    }
  }

  return {
    loading,
    subscriptions: cache,
    getSubscriptions,
    createSubscription,
    updateSubscription,
    modifySubscription,
    cancelSubscription,
    reactivateSubscriptionRenewal,
    renewSubscription,
    
    createSubscriptionRequest,
    getSubscriptionRequests,
    cancelSubscriptionRequest,
    requestExtensionQuote,

    // stripe
    goToStripeCheckout,
    getStripeCheckoutStatus
  };
});
